import { ofType } from 'redux-observable';
import { from, Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { requestGridItemsBySelection } from '../actions/grid-screen';
import {
    PRICES_WIZARD_SAVE_PRICES,
    PricesWizardGeneralError,
    pricesWizardGeneralError,
    PricesWizardPricesSaved,
    pricesWizardPricesSaved
} from '../actions/grid-wizards';
import {
    mapPricesMasterDataToAutoBidsInputApiData,
    mapPricesMasterDataToBidsInputApiData
} from '../mappers/phrase/phrase';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { NotSyncedError } from '../service/Errors';
import {
    getPricesWizardSavedSuccessMessage,
    showRequestResultNoticeMessage
} from '../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { fireMetrika } from '../service/Metrika/metrika';
import { extractKeywordsIds } from '../service/PhrasesHelpers/PhrasesHelpers';
import { PricesWizardHelpers } from '../service/PricesWizardHelpers/PricesWizardHelpers';
import '../service/Rx';
import { Phrase } from '../typings/domain';
import { DnaEpic, DnaEpicAction } from '../typings/epic';
import { setAutoBidsMutation, setBidsMutation } from '../typings/graphql-grid';
import { PricesWizardType } from '../typings/inspector';

const acceptPricesWizardChanges: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PRICES_WIZARD_SAVE_PRICES),
        mergeMap(
            (): DnaEpicAction => {
                const state = store$.value;
                const sourceParams = selectGridSourceParams(state);
                const {
                    selectionInfo: { phrases: phrasesIds },
                    hasObjectsOverLimit,
                    gridData: { total, data: gridData }
                } = state.grid;
                const { length: phrasesCount } = phrasesIds;

                const [mcBannerPhraseIds, otherPhraseIds] = extractKeywordsIds(gridData as Phrase[], phrasesIds);

                const data = PricesWizardHelpers.mapPricesWizardStateToExportData(state.grid.pricesWizardData);

                let responsePromise: Promise<setBidsMutation | setAutoBidsMutation>;

                if (data.wizardType === PricesWizardType.simple) {
                    responsePromise = deps.pricesWizardMutationExecutor.setBids(
                        mapPricesMasterDataToBidsInputApiData(data, phrasesIds),
                        sourceParams.login
                    );
                } else {
                    const [input, mcBannerInput] = mapPricesMasterDataToAutoBidsInputApiData(
                        data,
                        otherPhraseIds,
                        mcBannerPhraseIds
                    );
                    responsePromise = deps.pricesWizardMutationExecutor.setAutoBids(
                        input,
                        mcBannerInput,
                        sourceParams.login
                    );
                }

                return from(responsePromise).pipe(
                    mergeMap(
                        (response): DnaEpicAction => {
                            let updatedKeywords;

                            if ('setBids' in response) {
                                updatedKeywords = response.setBids.showConditionIds;
                            } else {
                                updatedKeywords = [
                                    ...response.setAutoBids.keywordIds,
                                    ...response.setMcBannerAutoBids.keywordIds
                                ];
                            }
                            const setCount = (updatedKeywords || []).length;
                            const needObjectsOverLimitNotification = total === phrasesCount && hasObjectsOverLimit;
                            const successMessage = getPricesWizardSavedSuccessMessage({
                                phrasesCount: phrasesCount,
                                setCount,
                                needObjectsOverLimitNotification
                            });

                            showRequestResultNoticeMessage(phrasesCount, setCount, successMessage);

                            fireMetrika({
                                pricesWizard: {
                                    saved: data
                                }
                            });
                            return [
                                pricesWizardPricesSaved(),
                                requestGridItemsBySelection('phrases', { phrases: phrasesIds })
                            ];
                        }
                    ),
                    catchError(
                        (e): Observable<PricesWizardGeneralError | PricesWizardPricesSaved> => {
                            if (e instanceof NotSyncedError) {
                                return Observable.of(pricesWizardPricesSaved());
                            } else {
                                return Observable.of(pricesWizardGeneralError(e.getMessage()));
                            }
                        }
                    )
                );
            }
        )
    );
};

export { acceptPricesWizardChanges };
